import { useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useGlobalEvent } from '@highlight/ipc/react'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useSetSidepanelHotkeyOpened } from '@renderer/overlay/Sidepanel/atoms'
import { useViewActions } from '@renderer/overlay/Sidepanel/view-atoms'
import { START_CHAT_EVENT, type StartChatEventPayload } from '@renderer/chat/integrations/conversation-action'
import { closeOpenHostedViews } from '@renderer/overlay/Sidepanel/components/view-popover'

/**
 * Opens a chat in the sidepanel on request from any window — a conversation
 * action invoked in a popout lands its chat here — and opens the panel on it:
 * the chat the action already started, or a new one whose composer holds the
 * message for the user to send.
 */
export function useStartChatEvent() {
  const { openView } = useViewActions()
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()

  const onStartChat = useCallback(
    (payload: StartChatEventPayload) => {
      if (!payload) return
      if (!('chatId' in payload) && !payload.prompt) return

      const openChat = () => {
        setHotkeyOpened(true)
        setSidepanelState('open')
        if ('chatId' in payload) {
          openView(
            'chat',
            { chatId: payload.chatId, instantScrollToBottom: true },
            { sourceLabel: payload.sourceLabel },
          )
          return
        }
        // Its own entry, so an unsent draft in an open New Chat isn't overwritten.
        openView(
          'chat',
          {
            isNew: true,
            draftId: uuidv4(),
            composerDraft: {
              text: payload.prompt,
              context: payload.context,
              contextLabel: payload.contextLabel,
              skills: payload.skills,
            },
          },
          { sourceLabel: payload.sourceLabel },
        )
      }

      // The chat takes the panel over, so a View hosted in a popover beside it
      // goes first — one close here rather than every control that starts a
      // chat remembering to shut its own host. The chat waits on that View's
      // leave guard: cancelling the confirmation keeps the user where they
      // are instead of opening a chat beside a View about something else.
      closeOpenHostedViews(openChat)
    },
    [openView, setSidepanelState, setHotkeyOpened],
  )

  useGlobalEvent(START_CHAT_EVENT, onStartChat)
}
